import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserCheck,
  FaClock,
} from "react-icons/fa";
import {
  FiEdit3,
  FiTrash2,
  FiMessageSquare,
  FiPackage,
  FiActivity,
  FiCreditCard,
  FiCheckCircle,
  FiClock as FiClockIcon,
} from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAccountOfficerCustomerOrderHistory } from "../../redux/thunk";
import axios from "axios";
import LoadingSpinner from "../../components/elements/LoadingSpinner";

const AccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((store) => store.auth);
  const {
    selectedCustomerHistory: customer,
    selectedCustomerHistoryStatus: loadingStatus,
  } = useAppSelector((store) => store.accountOfficer);

  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getAccountOfficerCustomerOrderHistory(id));
    }
  }, [id, dispatch]);

  const loading = loadingStatus === "loading" || localLoading;

  const formatNaira = (value: number) => {
    if (!value || value === 0) return "0";
    return `N${Number(value).toLocaleString()}`;
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/10">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="w-full bg-[#FAFAFA] min-h-screen p-4 md:p-8">
      {/* Header Area */}
      <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6 px-1">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Account Management
          </h1>
          <p className="text-gray-400 text-sm mt-1 font-medium">
            Manage platform news and announcements
          </p>
        </div>
      </div>

      {/* Top Profile Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        {/* Identity Card */}
        <div className="md:col-span-5 bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex items-center gap-6 relative overflow-hidden">
          <div className="absolute top-4 left-0 w-1 h-12 bg-[#008751] rounded-r-lg"></div>
          <div className="w-16 h-16 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 border-2 border-white shadow-sm ml-2">
            <img
              src={`https://ui-avatars.com/api/?name=${customer?.name || "Customer"}&background=f3f4f6&color=4b5563&bold=true&font-size=0.33`}
              alt="avatar"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-900 leading-tight">
              {customer?.name}
            </h2>
            <p className="text-gray-400 text-xs font-medium mt-1">
              Customer Profile
            </p>
          </div>
        </div>

        {/* Total Orders Card */}
        <div className="md:col-span-3 bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex flex-col justify-between relative">
          <div className="flex justify-between items-start">
            <p className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">
              Total Orders
            </p>
            <div className="w-6 h-6 rounded-full border border-gray-100 flex items-center justify-center">
              <FiEdit3 className="text-gray-300 text-[10px]" />
            </div>
          </div>
          <h3 className="text-3xl font-black text-gray-900">
            {customer?.orderHistory?.length || 0}
          </h3>
        </div>

        {/* Total Spend Card */}
        <div className="md:col-span-4 bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex flex-col justify-between relative">
          <div className="flex justify-between items-start">
            <p className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">
              Life-time Orders
            </p>
            <FiActivity className="text-gray-300 text-xs" />
          </div>
          <div className="flex items-baseline gap-1">
            <h3 className="text-3xl font-black text-gray-900 leading-none">
              {customer?.orderHistory?.length || 0}
            </h3>
            <span className="text-sm font-bold text-gray-400">Total</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Personal Details Column */}
        <div className="lg:col-span-8 flex flex-col gap-8">
          {/* Details Section */}
          <div className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
            <div className="mb-10">
              <h3 className="text-lg font-black text-[#008751] uppercase tracking-wider">
                Personal Details
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
              <DetailItem
                icon={<FaEnvelope className="text-gray-300" />}
                label="Email Address"
                value={customer?.email}
              />
              <DetailItem
                icon={<FaPhone className="text-gray-300 transform rotate-90" />}
                label="Phone Number"
                value={customer?.phoneNumber}
              />
              <DetailItem
                icon={<FaCalendarAlt className="text-gray-300" />}
                label="Registered"
                value={
                  customer?.registrationDate
                    ? new Date(customer.registrationDate).toLocaleDateString()
                    : "N/A"
                }
              />
              <DetailItem
                icon={<FaUserCheck className="text-gray-300" />}
                label="Assigned to me"
                value={
                  customer?.assignedDate
                    ? new Date(customer.assignedDate).toLocaleDateString()
                    : "N/A"
                }
              />
              <DetailItem
                icon={<FiActivity className="text-gray-300" />}
                label="Referral Code"
                value={customer?.officerReferralCode}
              />
            </div>
          </div>

          {/* Order History Table Section */}
          <div className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
            <div className="mb-10">
              <h3 className="text-lg font-black text-gray-900 tracking-tight">
                Order History
              </h3>
              <p className="text-gray-400 text-xs font-medium mt-1">
                All orders placed by this customer (
                {customer?.orderHistory?.length || 0})
              </p>
            </div>

            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full text-left min-w-[600px]">
                <thead className="text-gray-300 text-[10px] font-black uppercase tracking-widest border-b border-gray-50">
                  <tr>
                    <th className="px-4 pb-6">Order ID</th>
                    <th className="px-4 pb-6">Date</th>
                    <th className="px-4 pb-6">Items</th>
                    <th className="px-4 pb-6 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {customer?.orderHistory?.map((order: any) => (
                    <OrderRow
                      key={order.orderId}
                      id={order.orderId.substring(0, 8)}
                      date={new Date(order.orderDate).toLocaleDateString()}
                      items={order.orderItems
                        .map((item: any) => `${item.name} (${item.quantity})`)
                        .join(", ")}
                      status={
                        order.status
                          ? order.status.charAt(0).toUpperCase() +
                            order.status.slice(1)
                          : "Unknown"
                      }
                    />
                  ))}
                  {(!customer?.orderHistory ||
                    customer.orderHistory.length === 0) && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-10 text-center text-gray-400 font-medium"
                      >
                        No order history found for this customer.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Sidebar Column */}
        <div className="lg:col-span-4 flex flex-col gap-8">
          {/* Notes Section */}
          <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100 flex flex-col h-fit">
            <div className="mb-2">
              <h3 className="text-lg font-black text-gray-900">
                Private Notes
              </h3>
              <p className="text-gray-400 text-xs font-medium tracking-tight">
                Add notes about this customer (only visible to you)
              </p>
            </div>

            <div className="mt-8 relative group">
              <textarea
                placeholder="Add notes about this customer's requests, preferences, follow up notes"
                className="w-full h-48 p-8 bg-[#F8F9FA] border-none rounded-[2rem] text-xs font-medium placeholder:text-gray-300 focus:ring-1 focus:ring-green-500/20 focus:bg-white transition-all resize-none shadow-sm"
              ></textarea>
            </div>

            <button className="w-full mt-8 py-5 bg-[#008751] text-white font-black rounded-2xl text-sm transition-all hover:bg-[#007043] hover:shadow-xl hover:shadow-green-100 active:scale-[0.98]">
              Save Notes
            </button>
          </div>

          {/* Activity Feed Section */}
          <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100">
            <div className="mb-10">
              <h3 className="text-lg font-black text-gray-900">
                Activity Feed
              </h3>
              <p className="text-gray-400 text-xs font-medium mt-1">
                Customer activity history
              </p>
            </div>

            <div className="space-y-10 relative">
              {customer?.orderHistory
                ?.slice(0, 5)
                .map((order: any, idx: number) => (
                  <TimelineItem
                    key={order.orderId}
                    icon={<FiPackage className="text-green-500" />}
                    bgColor="bg-green-50"
                    name={customer.name}
                    action={`placed an order (${order.status})`}
                    time={new Date(order.orderDate).toLocaleString()}
                  />
                ))}
              <TimelineItem
                icon={<FiActivity className="text-orange-400" />}
                bgColor="bg-orange-50"
                name={customer?.name || "Customer"}
                action="was assigned to you"
                time={
                  customer?.assignedDate
                    ? new Date(customer.assignedDate).toLocaleString()
                    : "N/A"
                }
              />
              {(!customer?.orderHistory ||
                customer.orderHistory.length === 0) && (
                <div className="py-4 text-center">
                  <p className="text-xs text-gray-400 font-bold uppercase">
                    No recent activity
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ icon, label, value, span = 1 }: any) => (
  <div
    className={`flex items-start gap-4 ${span === 2 ? "md:col-span-2" : ""}`}
  >
    <div className="mt-1 text-gray-400 text-sm w-5">{icon}</div>
    <div className="flex-1">
      <p className="text-[11px] text-gray-400 font-medium mb-1 tracking-tight">
        {label}
      </p>
      <p className="text-sm font-semibold text-gray-800 leading-snug">
        {value || "Not provided"}
      </p>
    </div>
  </div>
);

const OrderRow = ({ id, date, items, amount, status }: any) => {
  const statusStyles: any = {
    Processing: "bg-[#F7931A] text-white",
    Completed: "#008751 text-white", // Wait, the image shows a solid green button-like badge
    Failed: "bg-[#EB5757] text-white",
  };

  // Correction for Completed style based on image
  const statusClass =
    status === "Completed" ? "bg-[#008751] text-white" : statusStyles[status];

  return (
    <tr className="group hover:bg-gray-50/50 transition-all border-b border-gray-50 last:border-0">
      <td className="px-4 py-6 text-sm font-semibold text-gray-500">{id}</td>
      <td className="px-4 py-6 text-sm text-gray-700">{date}</td>
      <td className="px-4 py-6 text-sm text-gray-600 font-medium">{items}</td>
      <td className="px-4 py-6 text-sm font-bold text-[#008751]">{amount}</td>
      <td className="px-4 py-6 text-right">
        <span
          className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${statusClass}`}
        >
          {status}
        </span>
      </td>
    </tr>
  );
};

const TimelineItem = ({ icon, bgColor, name, action, time }: any) => (
  <div className="flex gap-4 items-center">
    <div
      className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center flex-shrink-0`}
    >
      <div className="text-sm">{icon}</div>
    </div>
    <div className="flex-1">
      <p className="text-sm font-bold text-gray-900 leading-tight">
        <span className="font-extrabold">{name}</span> {action}
      </p>
      <p className="text-[11px] text-gray-400 font-medium mt-1">{time}</p>
    </div>
  </div>
);

export default AccountDetail;
